import { Select, SelectProps } from 'antd';
import { flatInfiniteData } from '@/utils/request/flatInfiniteData';
import { useState, useMemo } from 'react';
import InfiniteSelect from '@/components/_common/InfiniteSelect';
import { useDebounce } from '@/hooks';

import {
  GroupSelect,
  ListGroupsQuery,
} from '@/core/domain/operational/Groups/Groups.types';
import { useGroups } from '@/core/domain/operational/Groups/Groups.hooks';

type Props = Omit<SelectProps, 'options'> & {
  queryFilter?: ListGroupsQuery;
  selectedGroup?: GroupSelect[];
  searchBySubGroup?: boolean;
  labelValue?: boolean;
  onlyGroups?: boolean;
};

const GroupsInfiniteSelectField: React.FC<Props> = ({
  queryFilter,
  selectedGroup,
  labelValue,
  onlyGroups,
  searchBySubGroup = false,
  ...props
}) => {
  const [searchValue, setSearchValue] = useState<string>();
  const search = useDebounce(searchValue);

  const {
    data: groupsData,
    isLoading,
    hasNextPage,
    fetchNextPage,
  } = useGroups({
    ...queryFilter,
    active: true,
    rows: 30,
    search,
    searchBySubGroup,
  });

  const groups = useMemo(() => flatInfiniteData(groupsData), [groupsData]);

  const groupsWithSelected = useMemo(() => {
    if (
      selectedGroup &&
      !groups.some((group) =>
        group.subGroups?.some(
          (subGroup) => subGroup?.id === selectedGroup[0]?.subGroups[0]?.id,
        ),
      )
    ) {
      return [
        ...groups,
        {
          id: selectedGroup[0]?.id,
          name: selectedGroup[0]?.name,
          subGroups: [...(selectedGroup[0]?.subGroups || [])],
          active: true,
        },
      ];
    }
    return groups;
  }, [groups, selectedGroup]);

  const listSubGroupOptions = useMemo(() => {
    const groupsToRender = groupsWithSelected;
    return onlyGroups
      ? groupsToRender.map((group) => (
          <Select.Option key={group.id} value={group.id}>
            {group.name}
          </Select.Option>
        ))
      : groupsToRender.map((group) => (
          <Select.OptGroup key={group.id} label={group.name}>
            {group.subGroups?.map((subGroup) => (
              <Select.Option
                key={subGroup?.id}
                value={labelValue ? subGroup?.name : subGroup?.id}
              >
                {subGroup?.name}
              </Select.Option>
            ))}
          </Select.OptGroup>
        ));
  }, [onlyGroups, groupsWithSelected, labelValue]);

  return (
    <InfiniteSelect
      fetchNextPage={fetchNextPage}
      hasNextPage={hasNextPage}
      loading={isLoading}
      children={listSubGroupOptions}
      showSearch={true}
      searchValue={searchValue}
      filterOption={false}
      onSearch={setSearchValue}
      {...props}
    />
  );
};

export default GroupsInfiniteSelectField;
